import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import { motion } from 'motion/react';
import { StringParam, useQueryParam } from 'use-query-params';
import { VisuallyLogo } from '@/features/strapi/components/logo';
import { LogoUploadDropdown } from '@/components/logo-upload-dropdown';
import { isBrowser, nav } from '@/utils/browser';
import { AccountContext, useFeatureBit } from '@/features/account-context';
import { FeatureBit, StoreDetails } from '@/webapi/use-auth-api';
import { breakpoints } from '@/components/responsive';
import { GeneralStoreSettingsInner } from '@/webapi/use-store-settings-api';

const accountDomainTooltip = `account-domain-tooltip`;
const logoCacheKeyPrefix = `logo_section_uploaded_shop_logo`;

interface CachedLogo {
  uploadedShopLogo: string | null;
  shopifyLogo: string | null;
  siteName?: string | null;
  storeDomain?: string | null;
}

interface LogoState {
  alias: string;
  logo: CachedLogo;
}

interface LogoSectionProps {
  store?: StoreDetails;
}

const normalizeLogo = (logo?: string | null) => {
  const normalizedLogo = logo?.trim();
  return normalizedLogo || null;
};

const normalizeText = (value?: string | null) => {
  const normalizedValue = value?.trim();
  return normalizedValue || null;
};

const toLogoStateValue = (logo: string | null) => logo || undefined;

const getSiteName = (domain?: string) =>
  domain
    ?.replace(`https://`, ``)
    ?.replace(`www.`, ``)
    ?.replace(`.myshopify.com`, ``);

const getStoreDomain = (domain?: string) =>
  domain?.replace(`https://`, ``)?.replace(`www.`, ``);

const getLogoCacheKey = (alias: string) => `${logoCacheKeyPrefix}_${alias}`;

const getCachedLogo = (alias: string): CachedLogo | undefined => {
  if (!isBrowser() || !alias) return undefined;

  try {
    const value = localStorage.getItem(getLogoCacheKey(alias));
    if (!value) return undefined;
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const setCachedLogo = (alias: string, logo: CachedLogo): boolean => {
  if (!isBrowser() || !alias) return false;

  try {
    localStorage.setItem(getLogoCacheKey(alias), JSON.stringify(logo));
    return true;
  } catch {
    return false;
  }
};

const createCachedLogo = (
  uploadedShopLogo?: string | null,
  shopifyLogo?: string | null,
  siteName?: string | null,
  storeDomain?: string | null,
): CachedLogo => ({
  uploadedShopLogo: normalizeLogo(uploadedShopLogo),
  shopifyLogo: normalizeLogo(shopifyLogo),
  siteName: normalizeText(siteName),
  storeDomain: normalizeText(storeDomain),
});

const useLogoAlias = (storeAlias?: string) => {
  const [aliasQueryParam] = useQueryParam(`al`, StringParam);
  return aliasQueryParam || storeAlias || ``;
};

const useCachedLogo = (
  alias: string,
  generalStoreSettings: GeneralStoreSettingsInner | undefined,
  hideBrandLogo: boolean,
  siteName: string | undefined,
  storeDomain: string | undefined,
  canCacheStoreDetails: boolean,
) => {
  const getInitialLogo = () => {
    const cachedLogo = getCachedLogo(alias);
    if (cachedLogo) return cachedLogo;
    return createCachedLogo(
      generalStoreSettings?.uploaded_shop_logo,
      generalStoreSettings?.shop_logo,
      canCacheStoreDetails ? siteName : undefined,
      canCacheStoreDetails ? storeDomain : undefined,
    );
  };

  const [logoState, setLogoState] = useState<LogoState>(() => ({
    alias,
    logo: getInitialLogo(),
  }));

  const visibleLogo = useMemo(() => {
    if (logoState.alias === alias) return logoState.logo;
    return getCachedLogo(alias) || createCachedLogo();
  }, [alias, logoState]);

  const updateLogoState = (logo: CachedLogo) => {
    setLogoState({ alias, logo });
  };

  const updateCachedLogo = (
    uploadedShopLogo?: string | null,
    shopifyLogo?: string | null,
    nextSiteName: string | null | undefined = visibleLogo.siteName,
    nextStoreDomain: string | null | undefined = visibleLogo.storeDomain,
  ) => {
    const logo = createCachedLogo(
      uploadedShopLogo,
      shopifyLogo,
      nextSiteName,
      nextStoreDomain,
    );
    updateLogoState(logo);
    setCachedLogo(alias, logo);
  };

  useEffect(() => {
    if (hideBrandLogo) return;

    const cachedLogo = getCachedLogo(alias);
    if (cachedLogo) {
      updateLogoState(cachedLogo);
      return;
    }

    updateLogoState(createCachedLogo());
  }, [alias, hideBrandLogo]);

  useEffect(() => {
    if (hideBrandLogo || !generalStoreSettings || !canCacheStoreDetails) return;

    updateCachedLogo(
      generalStoreSettings.uploaded_shop_logo,
      generalStoreSettings.shop_logo,
      siteName,
      storeDomain,
    );
  }, [generalStoreSettings, hideBrandLogo]);

  return {
    uploadedShopLogo: toLogoStateValue(visibleLogo.uploadedShopLogo),
    shopifyLogo: toLogoStateValue(visibleLogo.shopifyLogo),
    cachedSiteName: toLogoStateValue(visibleLogo.siteName || null),
    cachedStoreDomain: toLogoStateValue(visibleLogo.storeDomain || null),
    updateCachedLogo,
  };
};

export const LogoSection: FC<LogoSectionProps> = ({ store }) => {
  const hideBrandLogo = useFeatureBit(FeatureBit.HIDE_BRAND_LOGO);
  const { generalStoreSettings, setGeneralStoreSettings } =
    useContext(AccountContext);
  const logoAlias = useLogoAlias(store?.alias);

  const siteName = useMemo(() => getSiteName(store?.domain), [store]);

  const storeDomain = useMemo(() => getStoreDomain(store?.domain), [store]);

  const storeMatchesActiveAlias = store?.alias === logoAlias;
  const {
    uploadedShopLogo,
    shopifyLogo,
    cachedSiteName,
    cachedStoreDomain,
    updateCachedLogo,
  } = useCachedLogo(
    logoAlias,
    generalStoreSettings,
    hideBrandLogo,
    siteName,
    storeDomain,
    storeMatchesActiveAlias,
  );
  const visibleSiteName = storeMatchesActiveAlias ? siteName : cachedSiteName;
  const visibleStoreDomain = storeMatchesActiveAlias
    ? storeDomain
    : cachedStoreDomain;

  const shouldShowSiteNameTooltip = useMemo(
    () => (visibleStoreDomain?.length || 0) > 20,
    [visibleStoreDomain],
  );

  const onLogoUpdate = (logo: string | undefined) => {
    updateCachedLogo(logo, shopifyLogo);
    if (setGeneralStoreSettings && generalStoreSettings) {
      setGeneralStoreSettings({
        ...generalStoreSettings,
        uploaded_shop_logo: logo,
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <Container>
        <VisuallyLogoButton onClick={() => nav(`/dashboard`)}>
          <VisuallyLogo small />
        </VisuallyLogoButton>
        {hideBrandLogo ? (
          <SiteName data-tip data-for={accountDomainTooltip}>
            {visibleSiteName || ``}
          </SiteName>
        ) : (
          <LogoUploadDropdown
            storeAlias={logoAlias}
            storeDomain={visibleStoreDomain || ``}
            shopifyLogo={shopifyLogo}
            uploadedShopLogo={uploadedShopLogo}
            currentSettings={generalStoreSettings}
            onLogoUpdated={onLogoUpdate}
          >
            {uploadedShopLogo ? (
              <BrandLogo
                src={uploadedShopLogo}
                alt={visibleSiteName || `Store logo`}
                data-tip
                data-for={accountDomainTooltip}
              />
            ) : (
              <SiteName data-tip data-for={accountDomainTooltip}>
                {visibleSiteName || ``}
              </SiteName>
            )}
          </LogoUploadDropdown>
        )}
        {shouldShowSiteNameTooltip && (
          // @ts-ignore
          <ReactTooltip
            id={accountDomainTooltip}
            place="bottom"
            backgroundColor="#fff"
            textColor="#000"
            className="domain-tooltip"
          >
            {visibleStoreDomain}
          </ReactTooltip>
        )}
      </Container>
    </motion.div>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 1.5rem;
`;

const VisuallyLogoButton = styled.button`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const SiteName = styled.span`
  font-family: 'Inter', serif;
  font-size: 1.8rem;
  text-transform: uppercase;
  font-weight: bold;
  color: black;
  max-width: 24rem;
  letter-spacing: -0.79px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BrandLogo = styled.img`
  display: block;
  ${breakpoints.down(`md`)} {
    transform: scale(0.8);
  }
`;
